import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * SeletorNoCelular — fileira de abas/seções que não cabe vira UMA linha
 * ("Financeiro: Visão Geral ▾") que abre a lista completa NO LUGAR.
 *
 * MOBILE-COMPLETO (M2, 22/10/2026): no celular, 8 das 11 abas do Financeiro e
 * 16 das 19 categorias da Ajuda ficavam fora da tela atrás de uma seta, e as
 * sub-seções da Manutenção (Chamados, Corretiva, Vistoria…) não tinham porta
 * nenhuma. ✅ Este componente só existe ABAIXO de `md` (`md:hidden`): no
 * computador as abas continuam exatamente como estão.
 *
 * Segue a regra do aviso de uma linha: fechado por padrão, abre no lugar (sem
 * modal, sem navegar), fecha ao escolher. Estado em React — ❌ zero
 * localStorage. `data-abre-navegacao` é o que o instrumento de medição do
 * celular (`scripts/mobile/medir-mobile.mjs`) toca para ver as opções.
 */
export interface OpcaoDoSeletor {
  chave: string;
  label: string;
  ativa: boolean;
  /** Navega (link) … */
  to?: string;
  /** … ou troca a aba sem navegar. */
  onSelecionar?: () => void;
}

export function SeletorNoCelular({
  rotulo,
  opcoes,
  className,
  testId,
}: {
  /** O nome da área ("Financeiro", "Categoria"). */
  rotulo: string;
  opcoes: OpcaoDoSeletor[];
  className?: string;
  testId?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const ativa = opcoes.find((o) => o.ativa);
  if (opcoes.length === 0) return null;

  return (
    <div className={cn('md:hidden', className)} data-testid={testId}>
      <button
        type="button"
        data-abre-navegacao
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-line bg-paper-surface px-3 text-left text-sm text-ink-1 dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
      >
        <span className="min-w-0 flex-1 truncate">
          <span className="text-ink-2 dark:text-ink-dark-2">{rotulo}: </span>
          <span className="font-semibold">{ativa?.label ?? 'escolha'}</span>
        </span>
        <span className="shrink-0 text-xs text-ink-3 dark:text-ink-dark-3">{opcoes.length}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-3 transition-transform dark:text-ink-dark-3', aberto && 'rotate-180')}
          aria-hidden
        />
      </button>
      {aberto && (
        <ul className="mt-1 overflow-hidden rounded-lg border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface">
          {opcoes.map((o) => {
            const classe = cn(
              'flex min-h-11 w-full items-center px-3 text-left text-sm',
              o.ativa
                ? 'bg-red-soft font-semibold text-red-deep dark:bg-red-soft-dark dark:text-red-glow'
                : 'text-ink-1 hover:bg-paper dark:text-ink-dark-1 dark:hover:bg-paper-dark',
            );
            return (
              <li key={o.chave} className="border-t border-line-soft first:border-t-0 dark:border-line-soft-dark">
                {o.to ? (
                  <Link
                    to={o.to}
                    aria-current={o.ativa ? 'page' : undefined}
                    onClick={() => setAberto(false)}
                    className={classe}
                  >
                    {o.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    aria-pressed={o.ativa}
                    onClick={() => {
                      o.onSelecionar?.();
                      setAberto(false);
                    }}
                    className={classe}
                  >
                    {o.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
